import { Fragment, useLayoutEffect, useMemo, useRef } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import gsap from 'gsap'
import { blend, getInviteFont, isDarkColor, luminance } from '../../lib/inviteStyle'
import {
  fitTitle,
  kickerFor,
  paintedAsset,
  paintedDate,
  readableOnPaper,
  splitNames,
  splitPlace,
  splitTwo,
} from '../../lib/paintedInvite'
import type { PaintedChoice, PaintedDef, PaintedMotion, PaintedPos, PaintedTypeset } from '../../lib/paintedInvite'
import PopupCard from './PopupCard'
import './painted-card.css'

/**
 * One painted invitation card: the template's artwork with the event's own
 * title, date and venue set on top, in the look's own typography.
 *
 * `mode="thumb"` is the cheap picker/gallery miniature — one pre-composed
 * thumbnail image (or the small plant / ink masks) and static type, so a
 * hundred can sit in a grid. `mode="full"` loads every layer separately and
 * brings the card to life with GSAP: an entrance (`play`), and then every
 * piece of artwork idles for as long as the card is on screen — it floats
 * (bobbing, swaying, drifting, spinning or hopping as its data says) and
 * breathes on a second, slower clock, so no card is ever a still. A photo
 * poster's picture pans instead, and its still life swells in place.
 * Reduced motion gets the still card.
 *
 * Everything is sized in cqw (percent of the card's width), so the same
 * markup is right at 180px in a grid and at full screen on the invitation.
 * Title sizes start from an estimate per face; in full mode the card then
 * measures itself and scales every title down (`--pc-fit`) until the words
 * fit — a host's own font or a long title never spills off the card.
 */

export type PaintedWords = {
  title: string
  honoree?: string | null
  date?: string | null
  time?: string | null
  location?: string | null
}

type Props = {
  def: PaintedDef
  choice: PaintedChoice
  words: PaintedWords
  lang?: string
  /** A host font override (INVITE_FONTS id); empty keeps the design's own type. */
  font?: string | null
  mode?: 'full' | 'thumb'
  /** Run the entrance when mounted (full mode). */
  play?: boolean
  /** The artwork alone, without the event's words. */
  bare?: boolean
  className?: string
  style?: CSSProperties
}

const SANS_KA = `'Noto Sans Georgian', system-ui, sans-serif`
const SERIF_KA = `'Noto Serif Georgian', Georgia, serif`

/**
 * Georgian companion face, then the Noto backstop — the arrangement
 * lib/inviteStyle.ts uses, so a painted card and a classic one set ქართული the
 * same way. Each face named below matches the Latin face in front of it;
 * index.css declares them all.
 *
 * Because every stack here ends in a Georgian face, the Georgian branches
 * further down can hand the card the same stack the Latin ones get: a Latin
 * display face matches no Georgian codepoint, so the companion takes over on
 * its own. They used to substitute a bare Noto instead, which is what made
 * every Georgian painted title — script, cartoon or poster — come out in the
 * same two faces.
 */
const kaStack = (face: string, base = SANS_KA) => `'${face}', ${base}`

type Typeset = {
  title: string
  weight: number
  upper?: boolean
  track?: string
  /** Average advance per em — what fitTitle needs to size a title. */
  k: number
  max: number
  write?: boolean
  kicker: string
  kickerSize: number
  kickerWeight: number
  kickerUpper?: boolean
  body: string
  serif?: boolean
}

// Measured per face: the k values keep the widest real titles inside the column.
const TYPESETS: Record<PaintedTypeset, Typeset> = {
  rounded: { title: `'Fredoka', ${kaStack('ALK Rounded', SANS_KA)}`, weight: 700, upper: true, track: '0.02em', k: 0.64, max: 15, kicker: `'Quicksand', ${kaStack('ALK Rounded', SANS_KA)}`, kickerSize: 3.6, kickerWeight: 600, body: `'Quicksand', ${kaStack('ALK Rounded', SANS_KA)}` },
  cartoon: { title: `'Luckiest Guy', ${kaStack('Geo Picasso', SANS_KA)}`, weight: 400, upper: true, track: '0.03em', k: 0.62, max: 15, kicker: `'Quicksand', ${kaStack('ALK Rounded', SANS_KA)}`, kickerSize: 3.6, kickerWeight: 600, body: `'Quicksand', ${kaStack('ALK Rounded', SANS_KA)}` },
  'bubbly-script': { title: `'Pacifico', ${kaStack('Geo Brush Script', SANS_KA)}`, weight: 400, k: 0.56, max: 14, write: true, kicker: `'Quicksand', ${kaStack('ALK Rounded', SANS_KA)}`, kickerSize: 3.6, kickerWeight: 600, body: `'Quicksand', ${kaStack('ALK Rounded', SANS_KA)}` },
  'glam-script': { title: `'Great Vibes', ${kaStack('MCh Vardi Serif', SERIF_KA)}`, weight: 400, k: 0.4, max: 18, write: true, kicker: `'Cormorant Garamond', ${kaStack('Geo Times', SERIF_KA)}`, kickerSize: 3.2, kickerWeight: 600, kickerUpper: true, body: `'Cormorant Garamond', ${kaStack('Geo Times', SERIF_KA)}`, serif: true },
  'garden-script': { title: `'Pinyon Script', ${kaStack('MCh Vardi Serif', SERIF_KA)}`, weight: 400, k: 0.46, max: 13, write: true, kicker: `'Cormorant Garamond', ${kaStack('Geo Times', SERIF_KA)}`, kickerSize: 3.1, kickerWeight: 600, kickerUpper: true, body: `'Cormorant Garamond', ${kaStack('Geo Times', SERIF_KA)}`, serif: true },
  festive: { title: `'Lobster', ${kaStack('SS Geo Hakuna', SANS_KA)}`, weight: 400, k: 0.48, max: 13, write: true, kicker: `'Caveat', ${kaStack('MG Niniko', SANS_KA)}`, kickerSize: 5.4, kickerWeight: 700, body: `'Quicksand', ${kaStack('ALK Rounded', SANS_KA)}` },
  deco: { title: `'Cinzel', ${kaStack('GL Kupiura', SERIF_KA)}`, weight: 700, upper: true, track: '0.04em', k: 0.8, max: 10.5, kicker: `'Montserrat', ${kaStack('Mersad', SANS_KA)}`, kickerSize: 2.9, kickerWeight: 500, kickerUpper: true, body: `'Montserrat', ${kaStack('Mersad', SANS_KA)}`, serif: true },
  rustic: { title: `'Abril Fatface', ${kaStack('Geo Picasso', SERIF_KA)}`, weight: 400, k: 0.56, max: 12, kicker: `'Caveat', ${kaStack('MG Niniko', SANS_KA)}`, kickerSize: 5.2, kickerWeight: 700, body: `'Cormorant Garamond', ${kaStack('Geo Times', SERIF_KA)}`, serif: true },
  soft: { title: `'Fredoka', ${kaStack('ALK Rounded', SANS_KA)}`, weight: 600, upper: true, track: '0.04em', k: 0.66, max: 10, kicker: `'Caveat', ${kaStack('MG Niniko', SANS_KA)}`, kickerSize: 5.8, kickerWeight: 700, body: `'Quicksand', ${kaStack('ALK Rounded', SANS_KA)}` },
  'poster-bold': { title: `'Bebas Neue', ${kaStack('LGV Bebas Neue', SANS_KA)}`, weight: 400, k: 0.42, max: 21, kicker: `'Quicksand', ${kaStack('ALK Rounded', SANS_KA)}`, kickerSize: 3.2, kickerWeight: 700, kickerUpper: true, body: `'Quicksand', ${kaStack('ALK Rounded', SANS_KA)}` },
}

const FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Abril+Fatface&family=Archivo:wght@300;500;800&family=Bebas+Neue&family=Caveat:wght@600;700&family=Cinzel:wght@500;700&family=Cormorant+Garamond:wght@500;600;700&family=Fredoka:wght@600;700&family=Great+Vibes&family=Lobster&family=Luckiest+Guy&family=Montserrat:wght@300;500;600;700&family=Mrs+Saint+Delafield&family=Noto+Sans+Georgian:wght@400;600;700;800&family=Noto+Serif+Georgian:wght@400;600;700&family=Pacifico&family=Pinyon+Script&family=Quicksand:wght@500;600;700&display=swap'

/** One stylesheet with every face the painted looks use; repeat calls are no-ops. */
export const loadPaintedFonts = () => {
  if (typeof document === 'undefined' || document.querySelector('link[data-painted-fonts]')) return
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = FONTS_HREF
  link.dataset.paintedFonts = '1'
  document.head.appendChild(link)
}

const contrast = (a: string, b: string) => {
  const la = luminance(a)
  const lb = luminance(b)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

const Letters = ({ text }: { text: string }) => {
  const words = text.split(' ')
  let i = 0
  return (
    <>
      {words.map((word, w) => (
        <Fragment key={w}>
          <span className="pc-word">
            {Array.from(word).map((ch) => (
              <span className="pc-l" data-pc="letter" key={i++}>
                {ch}
              </span>
            ))}
          </span>
          {w < words.length - 1 ? ' ' : ''}
        </Fragment>
      ))}
    </>
  )
}

type IdleMotion = PaintedMotion | 'swing' | 'plant' | 'rise'

/**
 * A piece of artwork: position box (entrance) › loop box (float) › pulse box
 * (breath) › content. The loop box carries the geometry the idle tweens need
 * — the piece's width and height as percentages of the card's width, and the
 * card edge it hangs from or stands on.
 */
const Piece = ({
  pos,
  w,
  r,
  motion,
  className = '',
  enter = 'enter',
  children,
}: {
  pos: PaintedPos
  w: number
  r: number
  motion?: IdleMotion
  className?: string
  enter?: string
  children: ReactNode
}) => (
  <span className={`pc-piece ${className}`} style={boxStyle(pos, w, r)} data-pc={enter}>
    <span
      className="pc-loop"
      data-motion={motion}
      data-w={w}
      data-h={(w * r).toFixed(2)}
      data-anchor={pos.bottom === undefined ? 'top' : 'bottom'}
    >
      <span className="pc-pulse">{children}</span>
    </span>
  </span>
)

/**
 * How a piece idles, for as long as the card is on screen. `x`, `y` and
 * `jump` are peak-to-peak travel in percent of the CARD's width — the tween
 * converts each into the piece's own percentage basis, so a narrow strand of
 * churchkhela and a wide khachapuri move by the same visible amount. `rot` is
 * peak-to-peak degrees. `breath` is the scale the piece swells to on a second,
 * slower clock, so float and breath drift in and out of step instead of
 * pumping together.
 */
type Idle = {
  x?: number
  y?: number
  rot?: number
  /** Seconds one half-cycle takes, picked per piece so no two cards beat alike. */
  dur?: [number, number]
  /** Wander to a fresh target each repeat instead of swinging between two. */
  wander?: boolean
  breath: number
  breathDur: [number, number]
  /** Turn and grow around the card edge the piece hangs from or stands on. */
  anchored?: boolean
  /** Explicit origins, for artwork whose anchor is not where it bends or swells. */
  pivot?: string
  grow?: string
  /** Height of the little jump the hoppers take between breaths. */
  jump?: number
}

const IDLES: Record<IdleMotion, Idle> = {
  bob: { y: 2.6, rot: 1.4, dur: [2.3, 3.1], breath: 1.045, breathDur: [1.5, 2.1], anchored: true },
  sway: { x: 1.4, rot: 4.4, dur: [2.6, 3.6], breath: 1.04, breathDur: [1.7, 2.3], anchored: true },
  breathe: { y: 1.8, rot: 1.4, dur: [2.7, 3.7], breath: 1.055, breathDur: [1.4, 1.9], anchored: true },
  drift: { x: 3.2, y: 2.8, rot: 6, dur: [2.4, 3.6], wander: true, breath: 1.05, breathDur: [1.5, 2.1] },
  spin: { y: 1.6, rot: 18, dur: [2.8, 3.8], breath: 1.05, breathDur: [1.7, 2.3] },
  hop: { x: 1.2, y: 1, rot: 1.4, dur: [2.7, 3.7], breath: 1.035, breathDur: [1.2, 1.7], anchored: true, jump: 5.4 },
  // The plate's stem meets the card's foot left of centre, so that is where
  // the whole plant bends from.
  plant: { x: 0.6, rot: 3.6, dur: [4.6, 6], breath: 1.022, breathDur: [2.6, 3.4], pivot: '35% 100%', grow: '35% 100%' },
  swing: { y: 0.9, rot: 3, dur: [3.2, 4.4], breath: 1.02, breathDur: [2.4, 3.2], anchored: true },
  // The photo poster's still life sits on its own photograph, so it only ever
  // grows, from near its centre, and never past what the cut-out's feathered
  // margin covers (see the invariant in components/AGENT.md).
  rise: { breath: 1.04, breathDur: [1.8, 2.4], grow: '50% 55%' },
}

const spills = (el: HTMLElement | null, box: HTMLElement | null) =>
  !!el && !!box && (el.offsetLeft < -1 || el.offsetLeft + el.scrollWidth > box.clientWidth + 1)

const wider = (el: HTMLElement | null) => !!el && el.scrollWidth > el.clientWidth + 1

// Line boxes are shorter than the glyphs in them (script capitals, Georgian
// ascenders), so the words keep 5% of the band free rather than filling it.
const BAND_ROOM = 0.95

/** Layout sizes only (offset*, scroll*), so the entrance's transforms never skew the measurement. */
const taller = (box: HTMLElement | null) => {
  const kids = box ? (Array.from(box.children) as HTMLElement[]) : []
  if (!kids.length) return false
  const first = kids[0]
  const last = kids[kids.length - 1]
  return last.offsetTop + last.offsetHeight - first.offsetTop > box!.clientHeight * BAND_ROOM
}

const overflows = (root: HTMLElement, look: PaintedDef['look']) => {
  const one = (sel: string) => root.querySelector<HTMLElement>(sel)
  if (look === 'watercolor') return taller(one('.pc-copy')) || spills(one('.pc-title'), one('.pc-copy'))
  if (look === 'botanical') {
    const copy = one('.pc-bcopy')
    return wider(one('.pc-n1')) || wider(one('.pc-n2')) || (!!copy && copy.offsetTop + copy.offsetHeight > root.clientHeight * 0.97)
  }
  if (look === 'photo') return spills(one('.pc-h1'), one('.pc-headline')) || spills(one('.pc-h2'), one('.pc-headline'))
  return taller(one('.pc-lcopy')) || spills(one('.pc-s1'), one('.pc-lcopy')) || spills(one('.pc-s2'), one('.pc-lcopy'))
}

const MIN_FIT = 0.25

const fitWords = (root: HTMLElement, look: PaintedDef['look']) => {
  root.style.setProperty('--pc-fit', '1')
  if (!root.clientWidth || !overflows(root, look)) return
  let lo = MIN_FIT
  let hi = 1
  for (let i = 0; i < 7; i += 1) {
    const mid = (lo + hi) / 2
    root.style.setProperty('--pc-fit', String(mid))
    if (overflows(root, look)) hi = mid
    else lo = mid
  }
  root.style.setProperty('--pc-fit', lo.toFixed(3))
}

const boxStyle = (pos: Record<string, number | undefined>, w: number, r: number): CSSProperties => {
  const s: Record<string, string> = { width: `${w}%`, aspectRatio: `1 / ${r}` }
  Object.entries(pos).forEach(([k, v]) => {
    if (v !== undefined) s[k] = `${v}%`
  })
  return s as CSSProperties
}

const PaintedArtCard = ({ def, choice, words, lang = 'en', font, mode = 'full', play = false, bare = false, className = '', style }: Props) => {
  const rootRef = useRef<HTMLDivElement>(null)
  const ka = lang === 'ka'
  const full = mode === 'full'
  const date = paintedDate(words.date, words.time, lang)
  const title = (words.title || '').trim()
  const place = (words.location || '').trim()
  const kicker = kickerFor(def, lang)
  const override = font ? getInviteFont(font) : null

  useLayoutEffect(() => {
    loadPaintedFonts()
  }, [])

  const wordsKey = `${title}|${words.date}|${words.time}|${place}|${words.honoree || ''}`

  useLayoutEffect(() => {
    const root = rootRef.current
    if (!full || bare || !root) return undefined
    const fit = () => fitWords(root, def.look)
    fit()
    const fonts = typeof document !== 'undefined' ? document.fonts : undefined
    let alive = true
    fonts?.ready.then(() => alive && fit())
    fonts?.addEventListener('loadingdone', fit)
    let width = root.clientWidth
    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => {
        if (root.clientWidth === width) return
        width = root.clientWidth
        fit()
      })
      : null
    observer?.observe(root)
    return () => {
      alive = false
      fonts?.removeEventListener('loadingdone', fit)
      observer?.disconnect()
    }
  }, [full, bare, def.id, def.look, lang, wordsKey, font, choice.colorway])

  useLayoutEffect(() => {
    const root = rootRef.current
    if (!full || !root) return undefined
    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia()
      mm.add('(prefers-reduced-motion: no-preference)', () => {
        const q = gsap.utils.selector(root)
        const rnd = gsap.utils.random
        if (play) {
          const tl = gsap.timeline()
          tl.from(q('[data-pc="bg"]'), { scale: 1.08, duration: 1.8, ease: 'power2.out' }, 0)
          tl.from(q('[data-pc="enter"]'), {
            scale: 0.25,
            opacity: 0,
            rotation: -8,
            duration: 0.9,
            ease: 'back.out(1.7)',
            stagger: 0.18,
            transformOrigin: '50% 90%',
          }, 0.3)
          tl.from(q('[data-pc="slide"]'), { xPercent: -10, opacity: 0, duration: 1.4, ease: 'power2.out' }, 0)
          tl.fromTo(q('[data-pc="draw"]'), { clipPath: 'inset(0% 100% 0% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.3, ease: 'power1.inOut' }, 0.1)
          tl.from(q('[data-pc="line"]'), { yPercent: 45, opacity: 0, duration: 0.6, ease: 'power2.out', stagger: 0.12 }, 0.35)
          tl.from(q('[data-pc="letter"]'), {
            yPercent: -70,
            opacity: 0,
            duration: 0.42,
            ease: 'back.out(2)',
            stagger: { each: 0.035, from: 'random' },
          }, 0.45)
          tl.fromTo(
            q('[data-pc="write"]'),
            { clipPath: 'inset(-30% 100% -30% -6%)' },
            { clipPath: 'inset(-30% -8% -30% -6%)', duration: 1.3, ease: 'power2.inOut', stagger: 0.55 },
            0.45,
          )
        }
        q('[data-motion]').forEach((el: HTMLElement) => {
          const idle = IDLES[el.dataset.motion as IdleMotion]
          if (!idle) return
          const pulse = el.firstElementChild as HTMLElement | null
          // The piece's own size as a share of the card's width, so travel
          // written in card percent covers the same distance on screen
          // whatever the cut-out's proportions.
          const wide = Number(el.dataset.w) || 30
          const tall = Number(el.dataset.h) || 30
          const half = (card = 0) => card / 2
          const px = (card = 0) => (card / wide) * 100
          const py = (card = 0) => (card / tall) * 100
          const hangs = el.dataset.anchor === 'top'
          const anchor = (top: string, bottom: string) => (idle.anchored ? (hangs ? top : bottom) : '50% 50%')
          const pivot = idle.pivot || anchor('50% 3%', '50% 100%')
          const grow = idle.grow || anchor('50% 10%', '50% 94%')
          const dur = idle.dur || [2.6, 3.6]

          if (idle.wander) {
            gsap.to(el, {
              xPercent: `random(${-px(half(idle.x))}, ${px(half(idle.x))})`,
              yPercent: `random(${-py(half(idle.y))}, ${py(half(idle.y))})`,
              rotation: `random(${-half(idle.rot)}, ${half(idle.rot)})`,
              duration: `random(${dur[0]}, ${dur[1]})`,
              ease: 'sine.inOut',
              repeat: -1,
              repeatRefresh: true,
            })
          } else if (idle.x || idle.y || idle.rot) {
            // Seeded part-way through its own cycle rather than delayed: two
            // pieces still never swing together, but a rebuild (a colourway
            // click reruns this effect) picks the motion up mid-stride instead
            // of parking the artwork at one extreme until the delay runs out.
            gsap
              .fromTo(
                el,
                { xPercent: px(half(idle.x)), yPercent: py(half(idle.y)), rotation: half(idle.rot) },
                {
                  xPercent: -px(half(idle.x)),
                  yPercent: -py(half(idle.y)),
                  rotation: -half(idle.rot),
                  duration: rnd(dur[0], dur[1]),
                  transformOrigin: pivot,
                  ease: 'sine.inOut',
                  yoyo: true,
                  repeat: -1,
                },
              )
              .progress(rnd(0, 1))
          }
          if (!pulse) return
          const breath = () => rnd(idle.breathDur[0], idle.breathDur[1])
          if (idle.jump) {
            // A hopper breathes once, then takes its little jump — one
            // timeline, so the squash never lands on top of a breath.
            gsap
              .timeline({ repeat: -1, delay: rnd(0.3, 2.4) })
              .set(pulse, { transformOrigin: grow })
              .to(pulse, { scale: idle.breath, duration: breath(), ease: 'sine.inOut' })
              .to(pulse, { scale: 1, duration: breath(), ease: 'sine.inOut' })
              .to(pulse, { yPercent: -py(idle.jump), scaleY: 1.06, scaleX: 0.95, duration: 0.26, ease: 'power2.out' })
              .to(pulse, { yPercent: 0, scaleY: 0.94, scaleX: 1.06, duration: 0.24, ease: 'power2.in' })
              .to(pulse, { scaleY: 1, scaleX: 1, duration: 0.5, ease: 'elastic.out(1, 0.4)' })
          } else {
            gsap
              .fromTo(
                pulse,
                { scale: 1 },
                {
                  scale: idle.breath,
                  duration: breath(),
                  transformOrigin: grow,
                  ease: 'sine.inOut',
                  yoyo: true,
                  repeat: -1,
                },
              )
              .progress(rnd(0, 1))
          }
        })
        // The photograph itself drifts, so a poster is never a still. The pan
        // stays inside the margin the scale-up leaves: at 1.05 the scene
        // overhangs the card by 2.5% a side, and it never travels past 1%.
        q('[data-pc="kenburns"]').forEach((el: HTMLElement) => {
          gsap.fromTo(
            el,
            { scale: 1.05, xPercent: -1, yPercent: -0.8 },
            { scale: 1.09, xPercent: 1, yPercent: 0.8, duration: 11, ease: 'sine.inOut', yoyo: true, repeat: -1 },
          )
        })
      })
    }, root)
    return () => ctx.revert()
    // Accent and paper only change CSS custom properties — no need to restart motion for them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [full, play, def.id, choice.colorway, lang, wordsKey, font])

  const content = useMemo(() => {
    const img = (stem: string) => paintedAsset(def, stem, full ? choice.colorway : null)
    const lazy = full ? undefined : ('lazy' as const)

    if (def.look === 'watercolor') {
      const ts = TYPESETS[def.typeset || 'rounded']
      const zp = def.zonePaper || def.paper
      const cx = def.cx ?? 12
      const width = 100 - cx * 2
      const titleFont = override ? override.display : ts.title
      const k = override ? 0.56 : ka ? 0.66 : ts.k
      const size = fitTitle(title, { max: ka ? Math.min(12, ts.max) : ts.max, min: 4.5, k, width, lines: 3 })
      const vars = {
        '--pc-name': readableOnPaper(choice.accent, zp, 3),
        '--pc-ink': def.ink,
        '--pc-halo': zp,
        '--pc-title-font': titleFont,
        '--pc-title-weight': String(ts.weight),
        '--pc-title-size': `${size.toFixed(2)}cqw`,
        '--pc-title-transform': ts.upper && !ka ? 'uppercase' : 'none',
        '--pc-title-track': ts.upper && !ka ? ts.track || '0' : '0',
        '--pc-kicker-font': override ? override.body : ts.kicker,
        '--pc-kicker-size': `${ka ? Math.min(3.8, ts.kickerSize) : ts.kickerSize}cqw`,
        '--pc-kicker-weight': String(ts.kickerWeight),
        '--pc-kicker-transform': ts.kickerUpper && !ka ? 'uppercase' : 'none',
        '--pc-body-font': override ? override.body : ts.body,
        '--pc-num-font': override ? override.display : `'Montserrat', ${kaStack('Mersad', SANS_KA)}`,
        '--pc-rule': def.ink,
      } as CSSProperties
      const titleNode = ts.write && !ka && !override
        ? <span className="pc-write" data-pc="write">{title}</span>
        : <Letters text={title} />
      return {
        vars,
        node: (
          <>
            {full ? (
              <span className="pc-bgwrap" data-pc="bg">
                <img className="pc-bg" src={img('bg')} alt="" draggable={false} />
              </span>
            ) : (
              <img className="pc-bg" src={paintedAsset(def, 'thumb', choice.colorway)} alt="" loading={lazy} draggable={false} />
            )}
            {full &&
              (def.pieces || []).map((p) => (
                <Piece key={p.key} pos={p.pos} w={p.w} r={p.r} motion={p.motion}>
                  <img src={img(p.key)} alt="" draggable={false} />
                </Piece>
              ))}
            <div className="pc-copy" style={{ top: `${def.zone?.[0] ?? 22}%`, bottom: `${100 - (def.zone?.[1] ?? 66)}%`, left: `${cx}%`, right: `${cx}%` }}>
              <p className="pc-kicker" data-pc="line">{kicker}</p>
              <p className="pc-title">{titleNode}</p>
              {date.day && def.date === 'line' && (
                <p className="pc-dateline" data-pc="line">
                  <span>{date.weekday}</span>
                  <span className="pc-dot">·</span>
                  <span>{ka ? `${date.day} ${date.month} ${date.year}` : `${date.month} ${date.day}, ${date.year}`}</span>
                  {date.time && <span className="pc-dot">·</span>}
                  {date.time && <span>{date.time}</span>}
                </p>
              )}
              {date.day && def.date !== 'line' && (
                <div className="pc-dateblock" data-pc="line">
                  <span className="pc-side">{date.weekday}</span>
                  <span className="pc-mid">
                    <span className="pc-mon">{date.month}</span>
                    <span className="pc-day">{date.day}</span>
                  </span>
                  <span className="pc-side">{date.time || date.year}</span>
                </div>
              )}
              {place && <p className="pc-venue" data-pc="line">{place}</p>}
            </div>
          </>
        ),
      }
    }

    if (def.look === 'botanical') {
      const paper = choice.paper
      const name = readableOnPaper(choice.accent, paper, 4.5)
      const caps = readableOnPaper(blend(name, paper, 0.32), paper, 3.4)
      const [n1, n2] = splitNames(title)
      const k = override ? 0.5 : ka ? 0.66 : 0.36
      const max = override ? 12 : ka ? 8.5 : 17
      const nameSize = Math.min(fitTitle(n1, { max, min: 6, k, width: 54, lines: 1 }), n2 ? fitTitle(n2, { max, min: 6, k, width: 54, lines: 1 }) : max)
      const p = def.plant!
      const vars = {
        '--pc-paper': paper,
        '--pc-name': name,
        '--pc-caps': caps,
        '--pc-name-size': `${nameSize.toFixed(2)}cqw`,
        '--pc-name-font': override ? override.display : `'Mrs Saint Delafield', ${kaStack('MCh Vardi Serif', SERIF_KA)}`,
        '--pc-body-font': override ? override.body : `'Cormorant Garamond', ${kaStack('Geo Times', SERIF_KA)}`,
      } as CSSProperties
      const writeName = !ka && !override
      return {
        vars,
        node: (
          <>
            <Piece
              className="pc-plant"
              pos={{ left: p.left, bottom: p.bottom }}
              w={p.width}
              r={p.r}
              motion={full ? 'plant' : undefined}
              enter="slide"
            >
              <img src={img(full ? 'plant' : 'plant-sm')} alt="" loading={lazy} draggable={false} />
            </Piece>
            <div className="pc-bcopy" style={{ left: `${def.copy!.left}%`, right: `${def.copy!.right}%`, top: `${def.copy!.top}%` }}>
              <p className="pc-names">
                <span className="pc-n1">{writeName ? <span className="pc-write" data-pc="write">{n1}</span> : n1}</span>
                {n2 && <span className="pc-n2">{writeName ? <span className="pc-write" data-pc="write">{n2}</span> : n2}</span>}
              </p>
              <p className="pc-caps" data-pc="line">{kicker}</p>
              {date.dotted && <p className="pc-bdate" data-pc="line">{date.dotted}</p>}
              {date.time && <p className="pc-caps pc-small" data-pc="line">{ka ? date.time : `at ${date.time.toLowerCase()}`}</p>}
              {place && (
                <p className="pc-caps pc-small" data-pc="line">
                  {splitPlace(place).map((l, i) => (
                    <span key={i} className="pc-block">{l}</span>
                  ))}
                </p>
              )}
            </div>
          </>
        ),
      }
    }

    if (def.look === 'photo') {
      const zp = def.zonePaper || def.paper
      const name = readableOnPaper(choice.accent, zp, 3)
      let second = blend(name, zp, 0.42)
      if (contrast(second, zp) < 1.9) second = blend(name, zp, 0.25)
      const [h1, h2] = splitTwo(title)
      const k = override ? 0.6 : ka ? 0.76 : 0.8
      const lineSize = (s: string) => Math.min(19, 91 / (Math.max(3, Array.from(s).length) * k)).toFixed(2)
      const sub = def.subject
      const vars = {
        '--pc-name': name,
        '--pc-name2': second,
        '--pc-head-font': override ? override.display : `'Archivo', ${kaStack('Mersad', SANS_KA)}`,
        '--pc-num-font': override ? override.display : `'Archivo', ${kaStack('Mersad', SANS_KA)}`,
        '--pc-body-font': override ? override.body : `'Archivo', ${kaStack('Mersad', SANS_KA)}`,
      } as CSSProperties
      const headline = (text: string, cls: string) => (
        <span className={cls} style={{ fontSize: `calc(${lineSize(text)}cqw * var(--pc-fit, 1))` }}>
          {Array.from(text).map((ch, i) => (ch === ' ' ? ' ' : <span className="pc-l" data-pc="letter" key={i}>{ch}</span>))}
        </span>
      )
      return {
        vars,
        node: (
          <>
            {full ? (
              <span className="pc-bgwrap">
                <span className="pc-scene" data-pc="kenburns">
                  <img className="pc-bg" src={img('bg')} alt="" draggable={false} />
                  {sub && (
                    <span className="pc-subject" style={boxStyle({ left: sub.left, top: sub.top }, sub.w, sub.r)}>
                      <span className="pc-loop" data-motion="rise" data-w={sub.w} data-h={(sub.w * sub.r).toFixed(2)} data-anchor="top">
                        <span className="pc-pulse">
                          <img src={img('subject')} alt="" draggable={false} />
                        </span>
                      </span>
                    </span>
                  )}
                </span>
              </span>
            ) : (
              <img className="pc-bg" src={paintedAsset(def, 'thumb')} alt="" loading={lazy} draggable={false} />
            )}
            <p className="pc-headline">
              {headline(h1, 'pc-h1')}
              {h2 && headline(h2, 'pc-h2')}
            </p>
            {date.short && <p className="pc-pdate" data-pc="line">{date.short}</p>}
            <p className="pc-pdet">
              {date.time && <span className="pc-block" data-pc="line">{ka ? `${date.time}-დან` : `Starting at ${date.time}`}</span>}
              {splitPlace(place).map((l, i) => (
                <span key={i} className="pc-block" data-pc="line">{l}</span>
              ))}
            </p>
          </>
        ),
      }
    }

    // line art
    const paper = choice.paper
    // --pc-ink paints the drawings AND the whole copy block (.pc-lt is ~3cqw),
    // so it takes the small-text bar, not the large-text one.
    const ink = readableOnPaper(choice.accent, paper, 4.5)
    const [s1, s2] = splitTwo(title)
    const k = override ? 0.5 : ka ? 0.66 : 0.42
    const s1Size = fitTitle(s1, { max: ka ? 9 : 18, min: 4, k, width: 58, lines: 1 })
    const s2Size = s2 ? Math.min(ka ? 7 : 11, fitTitle(s2, { max: 11, min: 3.5, k, width: 46, lines: 1 })) : 0
    const art = def.art!
    const vars = {
      '--pc-paper': paper,
      '--pc-ink': ink,
      '--pc-s1-size': `${s1Size.toFixed(2)}cqw`,
      '--pc-s2-size': `${s2Size.toFixed(2)}cqw`,
      '--pc-script-font': override ? override.display : `'Pinyon Script', ${kaStack('MCh Vardi Serif', SERIF_KA)}`,
      '--pc-body-font': override ? override.body : `'Montserrat', ${kaStack('Mersad', SANS_KA)}`,
    } as CSSProperties
    const mask = (stem: string) => ({ '--pc-mask': `url(${paintedAsset(def, full ? stem : `${stem}-sm`)})` }) as CSSProperties
    const honoree = (words.honoree || '').trim()
    const showHonoree = honoree && !title.toLowerCase().includes(honoree.toLowerCase())
    const printed = `${date.day} ${date.month}, ${date.year}`
    return {
      vars,
      node: (
        <>
          <Piece pos={art.garland.pos} w={art.garland.w} r={art.garland.r} motion={full ? 'swing' : undefined} enter="draw">
            <span className="pc-mask" style={mask('garland')} />
          </Piece>
          <Piece pos={art.left.pos} w={art.left.w} r={art.left.r} motion={full ? 'sway' : undefined}>
            <span className="pc-mask" style={mask('left')} />
          </Piece>
          <Piece pos={art.right.pos} w={art.right.w} r={art.right.r} motion={full ? 'breathe' : undefined}>
            <span className="pc-mask" style={mask('right')} />
          </Piece>
          <div className="pc-lcopy" style={{ top: `${def.zone?.[0] ?? 18}%`, bottom: `${100 - (def.zone?.[1] ?? 70)}%` }}>
            <p className="pc-lk" data-pc="line">{kicker}</p>
            {showHonoree && <p className="pc-ln" data-pc="line">{honoree}</p>}
            <p className="pc-script">
              <span className="pc-s1">{!ka && !override ? <span className="pc-write" data-pc="write">{s1}</span> : s1}</span>
              {s2 && <span className="pc-s2">{!ka && !override ? <span className="pc-write" data-pc="write">{s2}</span> : s2}</span>}
            </p>
            {date.time && <p className="pc-lt" data-pc="line">{ka ? date.time : `at ${date.time}`}</p>}
            {date.day && <p className="pc-lt" data-pc="line">{printed}</p>}
            {place && (
              <p className="pc-lt" data-pc="line">
                {splitPlace(place).map((l, i) => (
                  <span key={i} className="pc-block">{l}</span>
                ))}
              </p>
            )}
          </div>
        </>
      ),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [def, choice.accent, choice.paper, choice.colorway, lang, full, title, place, words.date, words.time, words.honoree, font])

  const dark =
    def.look === 'botanical' || def.look === 'line' ? isDarkColor(choice.paper) : !!def.dark
  return (
    <div
      ref={rootRef}
      className={`pc pc-${def.look}${full ? ' pc-full' : ' pc-thumb'}${dark ? ' pc-dark' : ''}${bare ? ' pc-bare' : ''} ${className}`}
      style={{ ...content.vars, ...style }}
      lang={ka ? 'ka' : 'en'}
    >
      {content.node}
    </div>
  )
}

const PaintedCard = (props: Props) => (props.def.look === 'popup' ? <PopupCard {...props} /> : <PaintedArtCard {...props} />)

export default PaintedCard
